import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogIn, Home } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Signed in successfully");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 bg-slate-950 lg:block">
        <div className="flex h-full flex-col justify-between p-12">
          <BrandLogo dark />
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-tight text-white">
              Welcome back to the<br /><span className="text-amber-500">Wonderstone SMME Portal</span>
            </h2>
            <p className="mt-4 max-w-md text-slate-400">
              Sign in to manage your supplier profile, respond to opportunities, or post
              tenders and RFQs as a buyer.
            </p>
          </div>
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Wonderstone Mine</p>
        </div>
      </div>

      <div className="relative flex w-full items-center justify-center bg-slate-50 px-6 lg:w-1/2">
        <Link to="/" data-testid="home-button" className="absolute right-6 top-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-amber-600"><Home className="h-4 w-4" /> Home</Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="w-full max-w-md">
          <div className="lg:hidden"><BrandLogo /></div>
          <h1 className="mt-8 font-display text-3xl font-bold text-slate-900">Sign in</h1>
          <p className="mt-1 text-slate-500">Enter your credentials to access the portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@company.co.za" className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm font-semibold text-amber-600 hover:text-amber-700" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm font-medium text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full gap-2 bg-slate-900 py-6 text-base font-semibold hover:bg-slate-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="font-semibold text-amber-600 hover:text-amber-700" data-testid="go-register-link">
              Register here
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
